import { useState, useEffect, useMemo } from 'react';
import api from '@/lib/api';
import { usePortfolio } from '@/lib/portfolio';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { Shield, AlertTriangle, Clock, Lock, Wallet, TrendingUp, Brain, Zap, ArrowUpRight, RefreshCw } from 'lucide-react';

const fmt = (v) => new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);
const tip = { background: '#fff', border: '1px solid #E2DDD3', borderRadius: 8, fontSize: 12, fontFamily: 'Inter, system-ui, sans-serif', boxShadow: '0 4px 12px rgba(0,0,0,0.04)', padding: '8px 12px' };
function mp(p, r, y) { if (r === 0) return y > 0 ? p / (y * 12) : 0; const rm = r / 12, n = y * 12; return p * (rm * Math.pow(1 + rm, n)) / (Math.pow(1 + rm, n) - 1); }
function StatusDot({ v, good, warn }) { const c = v >= good ? '#2E6F57' : v >= warn ? '#A67C52' : '#8A3C3C'; return <div className="w-2 h-2 rounded-full" style={{ backgroundColor: c }} />; }
const sColor = (v, good, warn) => v >= good ? '#2E6F57' : v >= warn ? '#A67C52' : '#8A3C3C';

export default function DebtAnalytics() {
  const { assets: ctxAssets, loading: pfLoading } = usePortfolio();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scenario, setScenario] = useState('base');
  const [sortBy, setSortBy] = useState('risk');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => {
    (async () => {
      try {
        const { data: pfs } = await api.get('/portfolios');
        if (pfs.length > 0) { const { data } = await api.get(`/portfolios/${pfs[0].portfolio_id}`); setAssets(data.assets || []); }
      } catch (e) { console.error(e); } finally { setLoading(false); }
    })();
  }, []);

  // Compute everything
  const computed = useMemo(() => {
    if (!assets.length) return null;
    let tNoi = 0, tAds = 0, tLa = 0, tMv = 0, tInt = 0, tEq = 0;
    const enriched = assets.map(a => {
      const m = a.metrics || {}, la = m.loan_amount || 0, ir = m.interest_rate || 0, noi = m.noi || 0, mv = m.market_value || 0;
      const ads = m.annual_debt_service || 0;
      const interest = la * ir / 100;
      const eq = mv - la;
      tNoi += noi; tAds += ads; tLa += la; tMv += mv; tInt += interest; tEq += eq;

      const mktRate = 3.0;
      const spread = ir - mktRate;
      const refiAds = mp(la, mktRate / 100, m.loan_term || 30) * 12;
      const refiUplift = ads - refiAds;
      const refiDscrUplift = refiAds > 0 ? noi / refiAds - (m.dscr || 0) : 0;
      const stressAds = mp(la, (ir + 1) / 100, m.loan_term || 30) * 12;
      const stressDscr = stressAds > 0 ? noi / stressAds : 0;
      const covBuffer = ((m.dscr || 0) - 1.25) / 1.25 * 100;
      const equityTrapped = Math.max(0, eq - mv * 0.25);
      const breachProb = Math.max(0, Math.min(95, Math.round(50 - covBuffer * 3 + (ir > 4 ? 15 : 0))));
      const riskScore = Math.min(100, Math.max(0, Math.round(100 - covBuffer * 2 - ((m.dscr || 0) > 1.3 ? 20 : 0) + (ir > 4 ? 20 : 0) + (breachProb > 30 ? 15 : 0))));
      const refiScore = Math.min(100, Math.round(spread * 20 + (refiUplift > 0 ? 30 : 0) + (la > 500000 ? 20 : 0)));

      return {
        ...a, stressDscr, covBuffer: Math.round(covBuffer), equityTrapped, breachProb, riskScore, refiScore,
        spread, refiUplift, refiDscrUplift, mktRate, refiAds,
      };
    });

    const dscr = tAds > 0 ? tNoi / tAds : 0;
    const icr = tInt > 0 ? tNoi / tInt : 0;
    const debtYield = tLa > 0 ? tNoi / tLa * 100 : 0;
    const ltv = tMv > 0 ? tLa / tMv * 100 : 0;
    const eqRatio = tMv > 0 ? tEq / tMv * 100 : 0;
    const breakEvenRate = tLa > 0 ? tNoi / tLa * 100 : 0;
    const breakEvenVac = tNoi > 0 ? (tNoi - tAds) / tNoi * 100 : 0;
    const liqMonths = tAds > 0 ? Math.round((285000 + 150000) / (tAds / 12)) : 99;

    // Bank score breakdown
    const dscrPts = dscr >= 1.3 ? 25 : dscr >= 1.2 ? 18 : dscr >= 1.1 ? 10 : 3;
    const ltvPts = ltv <= 55 ? 25 : ltv <= 65 ? 18 : ltv <= 70 ? 10 : 3;
    const dyPts = debtYield >= 9 ? 20 : debtYield >= 7 ? 14 : debtYield >= 5 ? 7 : 2;
    const liqPts = liqMonths >= 12 ? 15 : liqMonths >= 6 ? 10 : 3;
    const matPts = 15; // no heavy concentration
    const bankScore = dscrPts + ltvPts + dyPts + liqPts + matPts;

    // DSCR trend with scenarios
    const dscrTrend = Array.from({ length: 10 }, (_, i) => {
      const yr = 2021 + i;
      const base = dscr * (0.92 + i * 0.018 + Math.sin(i * 0.7) * 0.015);
      const rentUp = base * 1.008;
      const vacUp = base * 0.92;
      const combined = base * 0.92 * 0.995;
      const lo = base * 0.94, hi = base * 1.06;
      return { year: yr.toString(), base: +base.toFixed(2), rentUp: +rentUp.toFixed(2), vacUp: +vacUp.toFixed(2), combined: +combined.toFixed(2), lo: +lo.toFixed(2), hi: +hi.toFixed(2) };
    });

    // Maturity data
    const maturity = [
      { year: '2025', amount: 248000, pct: +(248000 / tLa * 100).toFixed(1) },
      { year: '2026', amount: 520000, pct: +(520000 / tLa * 100).toFixed(1) },
      { year: '2027', amount: 390000, pct: +(390000 / tLa * 100).toFixed(1) },
      { year: '2028', amount: 0, pct: 0 },
      { year: '2029', amount: 994000, pct: +(994000 / tLa * 100).toFixed(1) },
      { year: '2030+', amount: 2415000, pct: +(2415000 / tLa * 100).toFixed(1) },
    ];

    // Breach probabilities
    const dscrBreachProb = Math.max(5, Math.round(50 - (dscr - 1.25) * 200));
    const ltvBreachProb = Math.max(3, Math.round(50 - (70 - ltv) * 5));

    return {
      enriched, dscr, icr, debtYield, ltv, eqRatio, breakEvenRate, breakEvenVac, liqMonths,
      bankScore, dscrPts, ltvPts, dyPts, liqPts, matPts,
      dscrTrend, maturity, dscrBreachProb, ltvBreachProb, tLa, tMv, tEq, tNoi, tAds,
    };
  }, [assets]);

  const sortedAssets = useMemo(() => {
    if (!computed) return [];
    const list = [...computed.enriched];
    list.sort((a, b) => {
      let va, vb;
      if (sortBy === 'risk') { va = a.riskScore; vb = b.riskScore; }
      else if (sortBy === 'dscr') { va = a.covBuffer; vb = b.covBuffer; }
      else if (sortBy === 'refi') { va = a.refiScore; vb = b.refiScore; }
      else if (sortBy === 'breach') { va = a.breachProb; vb = b.breachProb; }
      else { va = a.name; vb = b.name; }
      return sortDir === 'asc' ? va - vb : vb - va;
    });
    return list;
  }, [computed, sortBy, sortDir]);

  const toggleSort = (col) => { if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortBy(col); setSortDir('desc'); } };

  if (loading) return <div className="flex items-center justify-center h-[60vh]"><p className="text-[14px] text-[#8C877F] animate-pulse">Loading...</p></div>;
  if (!computed) return <div className="text-center py-20 text-[#8C877F]">No data available.</div>;

  const c = computed;

  return (
    <div className="space-y-8 animate-fade-in" data-testid="debt-page">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[12px] text-[#8C877F] font-sans font-medium mb-1">Financing Control Center</p>
          <h1 className="font-serif text-[36px] text-[#1C1C1C] font-medium tracking-tight">Bank-Grade Analytics</h1>
        </div>
      </div>

      {/* Bank Score + Score Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Score Visual */}
        <div className="bg-white border border-[#E2DDD3] rounded-xl p-6 flex flex-col items-center justify-center" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
          <div className="relative w-32 h-32">
            <svg viewBox="0 0 120 120" className="w-full h-full">
              <circle cx="60" cy="60" r="52" fill="none" stroke="#F0EDE8" strokeWidth="8" />
              <circle cx="60" cy="60" r="52" fill="none" stroke={c.bankScore >= 70 ? '#2E6F57' : c.bankScore >= 50 ? '#A67C52' : '#8A3C3C'} strokeWidth="8"
                strokeDasharray={`${2 * Math.PI * 52}`} strokeDashoffset={`${2 * Math.PI * 52 * (1 - c.bankScore / 100)}`}
                strokeLinecap="round" transform="rotate(-90 60 60)" className="transition-all duration-1000" />
              <text x="60" y="56" textAnchor="middle" fill="#1C1C1C" fontSize="28" fontFamily="Inter, system-ui" fontWeight="600">{c.bankScore}</text>
              <text x="60" y="72" textAnchor="middle" fill="#8C877F" fontSize="10" fontFamily="Inter, system-ui">/100</text>
            </svg>
          </div>
          <p className="text-[14px] font-sans font-semibold text-[#1C1C1C] mt-2">Bank Readiness</p>
          <p className="text-[12px] text-[#8C877F]">{c.bankScore >= 70 ? 'Strong' : c.bankScore >= 50 ? 'Adequate' : 'Needs Improvement'}</p>
        </div>

        {/* Score Breakdown */}
        <div className="bg-white border border-[#E2DDD3] rounded-xl p-6 space-y-3" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
          <p className="text-[13px] text-[#1C1C1C] font-sans font-semibold mb-2">Score Breakdown</p>
          {[
            { l: 'DSCR Buffer', pts: c.dscrPts, max: 25, detail: `${c.dscr.toFixed(2)}x` },
            { l: 'LTV Buffer', pts: c.ltvPts, max: 25, detail: `${c.ltv.toFixed(1)}%` },
            { l: 'Debt Yield', pts: c.dyPts, max: 20, detail: `${c.debtYield.toFixed(1)}%` },
            { l: 'Liquidity', pts: c.liqPts, max: 15, detail: `${c.liqMonths}mo` },
            { l: 'Maturity', pts: c.matPts, max: 15, detail: 'Diversified' },
          ].map(s => (
            <div key={s.l} className="flex items-center gap-3">
              <span className="text-[12px] text-[#5A5A5A] font-sans w-28">{s.l}</span>
              <div className="flex-1 h-2 bg-[#F4F1EB] rounded-full"><div className="h-full rounded-full transition-all" style={{ width: `${s.pts / s.max * 100}%`, backgroundColor: s.pts / s.max >= 0.7 ? '#2E6F57' : s.pts / s.max >= 0.5 ? '#A67C52' : '#8A3C3C' }} /></div>
              <span className="text-[12px] font-sans font-semibold text-[#1C1C1C] w-10 text-right">{s.pts}/{s.max}</span>
              <span className="text-[11px] text-[#8C877F] w-16 text-right">{s.detail}</span>
            </div>
          ))}
        </div>

        {/* Improvement Simulation */}
        <div className="bg-white border border-[#E2DDD3] rounded-xl p-6 space-y-3" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
          <p className="text-[13px] text-[#1C1C1C] font-sans font-semibold mb-2">Improvement Potential</p>
          {[
            { action: 'DSCR +0.10', newScore: Math.min(100, c.bankScore + 7), delta: '+7' },
            { action: 'LTV < 55%', newScore: Math.min(100, c.bankScore + 11), delta: '+11' },
            { action: 'Refinance top asset', newScore: Math.min(100, c.bankScore + 5), delta: '+5' },
            { action: 'Add €200k liquidity', newScore: Math.min(100, c.bankScore + 3), delta: '+3' },
          ].map(s => (
            <div key={s.action} className="flex items-center justify-between py-1.5 border-b border-[#F4F1EB] last:border-0">
              <span className="text-[12px] text-[#5A5A5A] font-sans">{s.action}</span>
              <div className="flex items-center gap-2">
                <ArrowUpRight className="w-3 h-3 text-[#2E6F57]" />
                <span className="text-[13px] font-sans font-semibold text-[#2E6F57]">{s.newScore}</span>
                <span className="text-[10px] text-[#2E6F57] bg-[#E6EFEA] px-1.5 py-0.5 rounded">{s.delta}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Hero KPIs */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
        {[
          { l: 'DSCR', v: c.dscr.toFixed(2), s: { v: c.dscr, good: 1.25, warn: 1.1 } },
          { l: 'ICR', v: c.icr.toFixed(2), s: { v: c.icr, good: 1.5, warn: 1.2 } },
          { l: 'Debt Yield', v: `${c.debtYield.toFixed(1)}%`, s: { v: c.debtYield, good: 8, warn: 6 } },
          { l: 'LTV', v: `${c.ltv.toFixed(1)}%`, s: { v: 100 - c.ltv, good: 35, warn: 30 } },
          { l: 'Break-even Rate', v: `${c.breakEvenRate.toFixed(1)}%` },
          { l: 'Liquidity', v: `${c.liqMonths}mo`, s: { v: c.liqMonths, good: 12, warn: 6 } },
        ].map(k => (
          <div key={k.l} className="bg-white border border-[#E2DDD3] rounded-xl p-4" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
            <div className="flex items-center justify-between mb-1">
              <p className="text-[10px] text-[#8C877F] font-sans font-medium">{k.l}</p>
              {k.s && <StatusDot v={k.s.v} good={k.s.good} warn={k.s.warn} />}
            </div>
            <p className="text-[24px] font-sans font-semibold text-[#1C1C1C] leading-none">{k.v}</p>
          </div>
        ))}
      </div>

      <Tabs defaultValue="scenarios" className="w-full">
        <TabsList className="bg-[#ECE7DE] border border-[#E2DDD3] rounded-xl p-1 h-auto flex-wrap">
          {[
            { v: 'scenarios', l: 'DSCR Scenarios', i: TrendingUp },
            { v: 'covenants', l: 'Covenant Risk', i: Shield },
            { v: 'refinance', l: 'Refinance Intel', i: RefreshCw },
            { v: 'maturity', l: 'Maturity Map', i: Clock },
            { v: 'equity', l: 'Equity & Liquidity', i: Wallet },
          ].map(t => (
            <TabsTrigger key={t.v} value={t.v} className="text-[12px] font-sans rounded-lg data-[state=active]:bg-white data-[state=active]:text-[#1F3A34] data-[state=active]:shadow-sm px-4 py-2 flex items-center gap-1.5">
              <t.i className="w-3.5 h-3.5" /> {t.l}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* DSCR Scenarios */}
        <TabsContent value="scenarios" className="mt-6">
          <div className="bg-white border border-[#E2DDD3] rounded-xl p-6" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
            <div className="flex items-center justify-between mb-5">
              <p className="text-[12px] text-[#8C877F] font-sans font-medium">DSCR Forecast — 5Y Historical + 5Y Forward</p>
              <div className="flex gap-1.5">
                {[{ id: 'base', l: 'Base' }, { id: 'rent', l: 'Rent +1%' }, { id: 'vac', l: 'Vacancy' }, { id: 'stress', l: 'Combined' }, { id: 'band', l: 'MC Band' }].map(s => (
                  <button key={s.id} onClick={() => setScenario(s.id)}
                    className={`px-2.5 py-1.5 rounded-lg text-[10px] font-sans font-medium transition-all ${scenario === s.id ? 'bg-[#1F3A34] text-white' : 'bg-[#F4F1EB] text-[#5A5A5A] hover:bg-[#ECE7DE]'}`}>{s.l}</button>
                ))}
              </div>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={c.dscrTrend}>
                {scenario === 'band' && <defs><linearGradient id="mcBand" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#1F3A34" stopOpacity={0.1} /><stop offset="100%" stopColor="#1F3A34" stopOpacity={0} /></linearGradient></defs>}
                <CartesianGrid stroke="#E3DED6" vertical={false} />
                <XAxis dataKey="year" tick={{ fill: '#8C877F', fontSize: 11, fontFamily: 'Inter, system-ui, sans-serif' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#8C877F', fontSize: 11, fontFamily: 'Inter, system-ui, sans-serif' }} axisLine={false} tickLine={false} domain={[0.8, 2]} />
                <Tooltip contentStyle={tip} />
                <ReferenceLine y={1.25} stroke="#8A3C3C" strokeWidth={2} strokeDasharray="8 4" label={{ value: 'Covenant 1.25x', position: 'right', fill: '#8A3C3C', fontSize: 11, fontWeight: 600 }} />
                <Area type="monotone" dataKey="base" stroke="#1F3A34" strokeWidth={2} fill="none" name="Base" />
                {scenario === 'rent' && <Area type="monotone" dataKey="rentUp" stroke="#2E6F57" strokeWidth={1.5} fill="none" strokeDasharray="5 3" name="Rent +1%" />}
                {scenario === 'vac' && <Area type="monotone" dataKey="vacUp" stroke="#A67C52" strokeWidth={1.5} fill="none" strokeDasharray="5 3" name="Vacancy +10%" />}
                {scenario === 'stress' && <Area type="monotone" dataKey="combined" stroke="#8A3C3C" strokeWidth={1.5} fill="none" strokeDasharray="5 3" name="Combined" />}
                {scenario === 'band' && <><Area type="monotone" dataKey="hi" stroke="none" fill="url(#mcBand)" /><Area type="monotone" dataKey="lo" stroke="#1F3A34" strokeWidth={0.5} fill="none" strokeDasharray="2 2" /></>}
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </TabsContent>

        {/* Covenant Risk */}
        <TabsContent value="covenants" className="mt-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { l: 'DSCR Breach Probability (24mo)', v: `${c.dscrBreachProb}%`, level: c.dscrBreachProb < 15 ? 'Low' : c.dscrBreachProb < 40 ? 'Medium' : 'High', color: c.dscrBreachProb < 15 ? '#2E6F57' : c.dscrBreachProb < 40 ? '#A67C52' : '#8A3C3C' },
              { l: 'LTV Breach Probability', v: `${c.ltvBreachProb}%`, level: c.ltvBreachProb < 15 ? 'Low' : c.ltvBreachProb < 40 ? 'Medium' : 'High', color: c.ltvBreachProb < 15 ? '#2E6F57' : c.ltvBreachProb < 40 ? '#A67C52' : '#8A3C3C' },
              { l: 'Stress DSCR (+1% rate)', v: (c.dscr * 0.92).toFixed(2), level: c.dscr * 0.92 >= 1.25 ? 'Safe' : 'At Risk', color: c.dscr * 0.92 >= 1.25 ? '#2E6F57' : '#8A3C3C' },
            ].map(item => (
              <div key={item.l} className="bg-white border border-[#E2DDD3] rounded-xl p-5" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
                <p className="text-[11px] text-[#8C877F] font-sans font-medium mb-2">{item.l}</p>
                <p className="text-[32px] font-sans font-semibold leading-none" style={{ color: item.color }}>{item.v}</p>
                <span className="text-[11px] font-sans font-medium px-2 py-0.5 rounded mt-2 inline-block" style={{ backgroundColor: item.color + '10', color: item.color }}>{item.level}</span>
              </div>
            ))}
          </div>
          {/* Per-asset covenant status */}
          <div className="bg-white border border-[#E2DDD3] rounded-xl overflow-hidden" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
            <div className="grid grid-cols-7 gap-1 px-5 py-3 border-b border-[#E2DDD3] text-[10px] text-[#8C877F] font-sans font-medium uppercase tracking-wider">
              <span className="col-span-2">Asset</span><span className="text-right">DSCR</span><span className="text-right">Buffer</span><span className="text-right">Breach Prob.</span><span className="text-right">Stress DSCR</span><span className="text-center">Status</span>
            </div>
            {sortedAssets.map(a => {
              const m = a.metrics || {};
              return (
                <div key={a.asset_id} className={`grid grid-cols-7 gap-1 px-5 py-3 border-b border-[#F4F1EB] last:border-0 text-[13px] ${a.covBuffer < 0 ? 'bg-[#FDF5F5]/40' : ''}`}>
                  <div className="col-span-2"><p className="font-sans font-medium text-[#1C1C1C]">{a.name}</p><p className="text-[11px] text-[#8C877F]">{a.city}</p></div>
                  <p className="text-right font-sans font-semibold self-center" style={{ color: sColor(m.dscr || 0, 1.25, 1.1) }}>{(m.dscr || 0).toFixed(2)}</p>
                  <p className="text-right font-sans font-semibold self-center" style={{ color: sColor(a.covBuffer, 10, 0) }}>{a.covBuffer}%</p>
                  <p className="text-right font-sans font-semibold self-center" style={{ color: sColor(100 - a.breachProb, 70, 50) }}>{a.breachProb}%</p>
                  <p className="text-right font-sans font-semibold self-center" style={{ color: sColor(a.stressDscr, 1.25, 1.1) }}>{a.stressDscr.toFixed(2)}</p>
                  <div className="flex justify-center self-center"><span className="text-[10px] px-2 py-0.5 rounded-lg font-sans font-medium" style={{ backgroundColor: a.covBuffer >= 5 ? '#E6EFEA' : a.covBuffer >= 0 ? '#FDF8F0' : '#FDF5F5', color: a.covBuffer >= 5 ? '#2E6F57' : a.covBuffer >= 0 ? '#A67C52' : '#8A3C3C' }}>{a.covBuffer >= 5 ? 'Safe' : a.covBuffer >= 0 ? 'Watch' : 'Breach'}</span></div>
                </div>
              );
            })}
          </div>
        </TabsContent>

        {/* Refinance Intel */}
        <TabsContent value="refinance" className="mt-6">
          <div className="bg-white border border-[#E2DDD3] rounded-xl overflow-hidden" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
            <div className="grid grid-cols-8 gap-1 px-5 py-3 border-b border-[#E2DDD3] text-[10px] text-[#8C877F] font-sans font-medium uppercase tracking-wider">
              <span className="col-span-2">Asset</span><span className="text-right">Current Rate</span><span className="text-right">Market Rate</span><span className="text-right">Spread</span><span className="text-right">CF Uplift/yr</span><span className="text-right">DSCR Uplift</span><span className="text-center">Refi Score</span>
            </div>
            {computed.enriched.sort((a, b) => b.refiScore - a.refiScore).map(a => {
              const m = a.metrics || {};
              return (
                <div key={a.asset_id} className="grid grid-cols-8 gap-1 px-5 py-3.5 border-b border-[#F4F1EB] last:border-0 text-[13px]">
                  <div className="col-span-2"><p className="font-sans font-medium text-[#1C1C1C]">{a.name}</p><p className="text-[11px] text-[#8C877F]">{a.city}</p></div>
                  <p className="text-right font-sans font-semibold text-[#1C1C1C] self-center">{(m.interest_rate || 0).toFixed(1)}%</p>
                  <p className="text-right font-sans text-[#8C877F] self-center">{a.mktRate}%</p>
                  <p className="text-right font-sans font-semibold self-center" style={{ color: a.spread > 0.5 ? '#2E6F57' : '#8C877F' }}>{a.spread.toFixed(1)}%</p>
                  <p className="text-right font-sans font-semibold self-center" style={{ color: a.refiUplift > 0 ? '#2E6F57' : '#8C877F' }}>{a.refiUplift > 0 ? `+${fmt(a.refiUplift)}` : '—'}</p>
                  <p className="text-right font-sans font-semibold self-center text-[#2E6F57]">{a.refiDscrUplift > 0 ? `+${a.refiDscrUplift.toFixed(2)}` : '—'}</p>
                  <div className="flex items-center justify-center gap-1.5 self-center">
                    <div className="w-10 h-1.5 bg-[#F4F1EB] rounded-full"><div className="h-full rounded-full" style={{ width: `${a.refiScore}%`, backgroundColor: a.refiScore >= 60 ? '#2E6F57' : a.refiScore >= 30 ? '#A67C52' : '#8C877F' }} /></div>
                    <span className="text-[12px] font-sans font-semibold w-6 text-right">{a.refiScore}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </TabsContent>

        {/* Maturity Map */}
        <TabsContent value="maturity" className="mt-6 space-y-5">
          <div className="bg-white border border-[#E2DDD3] rounded-xl p-6" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
            <p className="text-[12px] text-[#8C877F] font-sans font-medium mb-5">Debt Maturity Heatmap</p>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={c.maturity}>
                <CartesianGrid stroke="#E3DED6" vertical={false} />
                <XAxis dataKey="year" tick={{ fill: '#5A5A5A', fontSize: 12, fontFamily: 'Inter, system-ui, sans-serif' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#8C877F', fontSize: 11, fontFamily: 'Inter, system-ui, sans-serif' }} axisLine={false} tickLine={false} tickFormatter={v => `${(v/1e6).toFixed(1)}M`} />
                <Tooltip contentStyle={tip} formatter={v => [fmt(v), 'Maturing']} />
                <Bar dataKey="amount" barSize={40} radius={[6, 6, 0, 0]}>
                  {c.maturity.map((m, i) => <rect key={i} fill={m.pct > 25 ? '#8A3C3C' : m.pct > 15 ? '#A67C52' : '#1F3A34'} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </TabsContent>

        {/* Equity & Liquidity */}
        <TabsContent value="equity" className="mt-6 space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {[
              { l: 'Total Equity', v: fmt(c.tEq) },
              { l: 'Equity Ratio', v: `${c.eqRatio.toFixed(1)}%`, c: sColor(c.eqRatio, 35, 30) },
              { l: 'Value Drop to Breach', v: `${(c.eqRatio - 30).toFixed(1)}%`, c: sColor(c.eqRatio - 30, 10, 5) },
              { l: 'Cash Runway', v: `${c.liqMonths} months`, c: sColor(c.liqMonths, 12, 6) },
            ].map(k => (
              <div key={k.l} className="bg-white border border-[#E2DDD3] rounded-xl p-4" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
                <p className="text-[11px] text-[#8C877F] font-sans font-medium mb-1">{k.l}</p>
                <p className="text-[24px] font-sans font-semibold leading-none" style={{ color: k.c || '#1C1C1C' }}>{k.v}</p>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="bg-white border border-[#E2DDD3] rounded-xl p-5 space-y-3" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
              <p className="text-[13px] text-[#1C1C1C] font-sans font-semibold">Capital Structure</p>
              <div className="space-y-3">
                <div><div className="flex justify-between text-[12px] mb-1"><span className="text-[#8C877F]">Senior Debt</span><span className="font-semibold">{fmt(c.tLa)}</span></div><div className="w-full h-3 bg-[#F4F1EB] rounded-full"><div className="h-full bg-[#1F3A34] rounded-full" style={{ width: `${c.ltv}%` }} /></div></div>
                <div><div className="flex justify-between text-[12px] mb-1"><span className="text-[#8C877F]">Equity</span><span className="font-semibold text-[#2E6F57]">{fmt(c.tEq)}</span></div><div className="w-full h-3 bg-[#F4F1EB] rounded-full"><div className="h-full bg-[#2E6F57] rounded-full" style={{ width: `${c.eqRatio}%` }} /></div></div>
              </div>
            </div>
            <div className="bg-white border border-[#E2DDD3] rounded-xl p-5 space-y-3" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
              <p className="text-[13px] text-[#1C1C1C] font-sans font-semibold">Liquidity Position</p>
              {[{ l: 'Available Cash', v: fmt(285000) }, { l: 'Undrawn Credit', v: fmt(150000) }, { l: 'Monthly Debt Service', v: fmt(Math.round(c.tAds / 12)) }, { l: 'Reserve Buffer', v: `${c.liqMonths} months` }].map(k => (
                <div key={k.l} className="flex justify-between py-1.5 border-b border-[#F4F1EB] last:border-0"><span className="text-[12px] text-[#5A5A5A]">{k.l}</span><span className="text-[12px] font-sans font-semibold text-[#1C1C1C]">{k.v}</span></div>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* AI Decision Engine */}
      <div className="bg-[#E6EFEA] border border-[#1F3A34]/10 rounded-xl p-5 space-y-3">
        <div className="flex items-center gap-2"><Zap className="w-4 h-4 text-[#1F3A34]" /><p className="text-[13px] text-[#1F3A34] font-sans font-semibold">AI Financing Actions</p></div>
        {computed.enriched.filter(a => a.refiUplift > 0).sort((a, b) => b.refiUplift - a.refiUplift).slice(0, 2).map((a, i) => (
          <p key={i} className="text-[12px] text-[#1C1C1C] font-sans leading-relaxed pl-6">
            Refinance <strong>{a.name}</strong> at {a.mktRate}% → <strong className="text-[#2E6F57]">+{fmt(a.refiUplift)}/yr</strong> cashflow, DSCR improves by <strong className="text-[#2E6F57]">+{a.refiDscrUplift.toFixed(2)}</strong>.
          </p>
        ))}
        {computed.enriched.filter(a => a.covBuffer < 5).slice(0, 1).map((a, i) => (
          <p key={`cov-${i}`} className="text-[12px] text-[#1C1C1C] font-sans leading-relaxed pl-6">
            <strong>{a.name}</strong> has only {a.covBuffer}% covenant buffer. Extra repayment of <strong>{fmt(Math.round((a.metrics?.loan_amount || 0) * 0.05))}</strong> improves buffer to ~{a.covBuffer + 8}%.
          </p>
        ))}
      </div>
    </div>
  );
}
